import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { LEGAL_VERSION } from '@/lib/legalContent';

const API = `${typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PLANS = {
    monthly: { label: 'Mensile', price: 100, per: '/mese', sub: 'Fatturato ogni mese' },
    yearly: { label: 'Annuale', price: 1000, per: '/anno', sub: 'Fatturato ogni anno', save: 200 },
};

const FEATURES = [
    'Gestione ordini, autisti e consegne',
    'Sincronizzazione Danea Easyfatt e Fatture in Cloud',
    'Documenti (DDT, Fatture, Listini)',
    'Portale clienti dedicato',
    'Import massivo anagrafica clienti',
];

export default function SignupPage() {
    const [plan, setPlan] = useState('monthly');
    const [form, setForm] = useState({ company_name: '', vat_number: '', email: '', password: '' });
    const [accept, setAccept] = useState(false);
    const [loading, setLoading] = useState(false);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!accept) { toast.error('Devi accettare Termini e Privacy Policy'); return; }
        if (!form.company_name || !form.vat_number || !form.email || !form.password) {
            toast.error('Compila tutti i campi'); return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/auth/signup`, {
                ...form, plan, accept_terms: accept, legal_version: LEGAL_VERSION, origin_url: window.location.origin,
            });
            const url = res.data.checkout_url;
            // In preview l'app gira dentro un iframe: Stripe Checkout non può essere
            // caricato in un iframe (schermata bianca). Navighiamo la finestra top.
            try {
                if (window.top && window.top !== window.self) {
                    window.top.location.href = url;
                } else {
                    window.location.href = url;
                }
            } catch {
                window.location.href = url;
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore nella registrazione');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4" data-testid="signup-page">
            <div className="w-full max-w-4xl grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-2xl">
                {/* Left: value + plan */}
                <div className="bg-[#1B4E8E] text-white p-8 flex flex-col">
                    <div className="flex items-center gap-3 mb-6">
                        <img src="/omniglide-icon.png" alt="OmniGlide" className="h-12 w-12 rounded-xl bg-white p-1 object-contain shrink-0" />
                        <div className="leading-tight">
                            <span className="font-bold text-lg block" style={{ fontFamily: 'Work Sans, sans-serif' }}>OmniGlide</span>
                            <span className="text-white/70 text-xs">Gestione fluida merci &amp; logistica</span>
                        </div>
                    </div>
                    <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Prova gratis 30 giorni</h1>
                    <p className="text-white/70 text-sm mb-6">Nessun costo per 30 giorni. Disdici quando vuoi.</p>

                    <div className="space-y-2 mb-6">
                        {Object.entries(PLANS).map(([key, p]) => (
                            <button
                                type="button"
                                key={key}
                                onClick={() => setPlan(key)}
                                data-testid={`plan-${key}`}
                                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${plan === key ? 'border-white bg-white/10' : 'border-white/20 hover:border-white/40'}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-semibold">{p.label}</span>
                                        {p.save && <span className="ml-2 text-xs bg-[#7DD3E8] text-[#0B3B66] font-bold px-2 py-0.5 rounded-full">Risparmi {p.save}€</span>}
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xl font-bold">{p.price}€</span>
                                        <span className="text-white/60 text-sm"> +IVA{p.per}</span>
                                    </div>
                                </div>
                                <p className="text-white/60 text-xs mt-1">{p.sub}</p>
                            </button>
                        ))}
                    </div>

                    <ul className="space-y-2 mt-auto">
                        {FEATURES.map((f) => (
                            <li key={f} className="flex items-center gap-2 text-sm text-white/80">
                                <Check className="h-4 w-4 text-[#7DD3E8] shrink-0" /> {f}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Right: form */}
                <div className="bg-white p-8">
                    <Card className="border-none shadow-none">
                        <CardContent className="p-0">
                            <h2 className="text-xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>Crea il tuo account</h2>
                            <p className="text-sm text-[#64748B] mb-5">Bastano pochi dati, il resto lo completi dopo.</p>
                            <form onSubmit={submit} className="space-y-4">
                                <div className="space-y-1">
                                    <Label>Ragione sociale</Label>
                                    <Input value={form.company_name} onChange={(e) => setField('company_name', e.target.value)} placeholder="Rossi Frutta Srl" data-testid="signup-company-name" />
                                </div>
                                <div className="space-y-1">
                                    <Label>Partita IVA</Label>
                                    <Input value={form.vat_number} onChange={(e) => setField('vat_number', e.target.value)} placeholder="12345678901" data-testid="signup-vat" />
                                </div>
                                <div className="space-y-1">
                                    <Label>Email</Label>
                                    <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="tu@azienda.it" data-testid="signup-email" />
                                </div>
                                <div className="space-y-1">
                                    <Label>Password</Label>
                                    <Input type="password" value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder="Almeno 8 caratteri" data-testid="signup-password" />
                                </div>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <Checkbox checked={accept} onCheckedChange={(v) => setAccept(v === true)} data-testid="signup-accept-terms" className="mt-0.5 data-[state=checked]:bg-[#1B4E8E] data-[state=checked]:border-[#1B4E8E]" />
                                    <span className="text-xs text-[#64748B]">Accetto i <Link to="/terms" target="_blank" className="text-[#1B4E8E] underline">Termini di Servizio</Link>, la <Link to="/privacy" target="_blank" className="text-[#1B4E8E] underline">Privacy Policy</Link> e il <Link to="/dpa" target="_blank" className="text-[#1B4E8E] underline">DPA</Link>.</span>
                                </label>
                                <Button type="submit" className="w-full bg-[#1B4E8E] hover:bg-[#143A6B]" disabled={loading} data-testid="signup-submit">
                                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Inizia la prova gratuita
                                </Button>
                                <p className="text-xs text-center text-[#64748B]">
                                    30 giorni gratis, poi {PLANS[plan].price}€ +IVA{PLANS[plan].per}. Ti chiederemo i dati della carta.
                                </p>
                            </form>
                            <p className="text-sm text-center text-[#64748B] mt-4">
                                Hai già un account? <Link to="/login" className="text-[#1B4E8E] font-medium hover:underline" data-testid="signup-login-link">Accedi</Link>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
